import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { getSets } from "../lib/api";
import { trackAddToCart } from "../lib/analytics";

const CartContext = createContext(null);
const STORAGE_KEY = "ta-cart-v1";

const _readStoredCart = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[cart] failed to parse stored cart:", err);
    }
    return [];
  }
};

export const CartProvider = ({ children }) => {
  // Hydrate synchronously from localStorage so a hard reload (or new tab)
  // never sees a flicker of empty cart that would clobber the persisted state.
  const [items, setItems] = useState(_readStoredCart);
  const [isOpen, setIsOpen] = useState(false);
  const [sets, setSets] = useState([]);

  useEffect(() => {
    getSets().then(setSets).catch(() => setSets([]));
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, { size, color, quantity = 1 }) => {
    setItems((curr) => {
      const key = `${product.id}|${size}|${color}`;
      const existing = curr.find((i) => i.key === key);
      if (existing) {
        return curr.map((i) => (i.key === key ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [
        ...curr,
        {
          key,
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.image,
          size,
          color,
          quantity,
        },
      ];
    });
    setIsOpen(true);
    trackAddToCart(product, { size, color, quantity });
  }, []);

  const updateQty = useCallback((key, delta) => {
    setItems((curr) =>
      curr
        .map((i) => (i.key === key ? { ...i, quantity: i.quantity + delta } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((curr) => curr.filter((i) => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  // Compute set discounts client-side (mirror of backend logic)
  const qtyById = items.reduce((acc, i) => {
    acc[i.product_id] = (acc[i.product_id] || 0) + i.quantity;
    return acc;
  }, {});
  const appliedSets = sets
    .map((s) => {
      const ok = s.product_ids.every((pid) => (qtyById[pid] || 0) >= 1);
      if (!ok) return null;
      const nSets = Math.min(...s.product_ids.map((pid) => qtyById[pid]));
      return { set: s, count: nSets, savings: s.discount * nSets };
    })
    .filter(Boolean);
  const setSavings = appliedSets.reduce((s, a) => s + a.savings, 0);
  const total = Math.max(0, subtotal - setSavings);

  const value = useMemo(
    () => ({ items, addItem, updateQty, removeItem, clearCart, subtotal, count, isOpen, setIsOpen, sets, appliedSets, setSavings, total, qtyById }),
    [items, addItem, updateQty, removeItem, clearCart, subtotal, count, isOpen, sets, appliedSets, setSavings, total, qtyById]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
